import { formatDistanceToNow } from 'date-fns'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useCampaign, useCancelCampaign } from '@/api/hooks/use-campaigns'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import { CampaignClicksTab } from '@/pages/campaigns/campaign-clicks-tab'
import { CampaignOverviewTab } from '@/pages/campaigns/campaign-overview-tab'
import { CampaignRecipientsTab } from '@/pages/campaigns/campaign-recipients-tab'
import { CampaignVariantStats } from '@/pages/campaigns/campaign-variant-stats'

type TabId = 'overview' | 'recipients' | 'clicks'

function formatRate(numerator: number, denominator: number): string {
  if (denominator === 0) return '0%'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

export function CampaignDetailPage(): React.JSX.Element {
  const { id } = useParams()
  const { data: campaign, isLoading } = useCampaign(id ?? '')
  const cancelCampaign = useCancelCampaign()
  const [activeTab, setActiveTab] = useState<TabId>('overview')

  if (isLoading || !campaign || !id) {
    return <div className="py-12 text-center text-text-tertiary">Loading campaign...</div>
  }

  const delivered = campaign.total_delivered
  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'recipients', label: 'Recipients' },
    { id: 'clicks', label: 'Click Analytics' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/campaigns" className="rounded-lg p-1.5 hover:bg-surface-2">
            <ArrowLeft className="h-5 w-5 text-text-secondary" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-text-primary">{campaign.name}</h1>
              <StatusBadge status={campaign.status}>{campaign.status}</StatusBadge>
            </div>
            <p className="text-sm text-text-secondary">
              {campaign.audience_name ?? 'Unknown audience'}
              {campaign.send_completed_at && (
                <>
                  {' '}
                  &middot; Sent{' '}
                  {formatDistanceToNow(new Date(campaign.send_completed_at), { addSuffix: true })}
                </>
              )}
            </p>
          </div>
        </div>
        {campaign.status === 'sending' && (
          <button
            type="button"
            onClick={() => cancelCampaign.mutate(id)}
            disabled={cancelCampaign.isPending}
            className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {cancelCampaign.isPending ? 'Cancelling...' : 'Cancel Send'}
          </button>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <MetricCard label="Recipients" value={campaign.total_recipients.toLocaleString()} />
        <MetricCard
          label="Delivered"
          value={formatRate(delivered, campaign.total_recipients)}
          sub={`${delivered.toLocaleString()} emails`}
          variant="success"
        />
        <MetricCard
          label="Opened"
          value={formatRate(campaign.total_opened, delivered)}
          sub={`${campaign.total_opened.toLocaleString()} opens`}
        />
        <MetricCard
          label="Clicked"
          value={formatRate(campaign.total_clicked, delivered)}
          sub={`${campaign.total_clicked.toLocaleString()} clicks`}
        />
        <MetricCard
          label="Bounced"
          value={formatRate(campaign.total_bounced, campaign.total_recipients)}
          sub={`${campaign.total_bounced.toLocaleString()} bounces`}
          variant={campaign.total_bounced > 0 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Unsubscribed"
          value={formatRate(campaign.total_unsubscribed, delivered)}
          sub={`${campaign.total_unsubscribed.toLocaleString()} unsubs`}
          variant={campaign.total_unsubscribed > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* A/B variant results */}
      {campaign.variants && campaign.variants.length > 0 && campaign.status !== 'draft' && (
        <CampaignVariantStats campaignId={id} variants={campaign.variants} />
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-3">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <CampaignOverviewTab campaign={campaign} />}
      {activeTab === 'recipients' && <CampaignRecipientsTab campaignId={id} />}
      {activeTab === 'clicks' && (
        <CampaignClicksTab campaignId={id} campaignCreatedAt={campaign.created_at} />
      )}
    </div>
  )
}
